import { Endpoints } from '@/network';
import { apiClient } from '@/services/apiClients';

export interface OrganizationProfile {
  id?: number | string;
  name?: string;
  code?: string;
  brand_name?: string;
  gst_number?: string;
  org_type?: string;
  department?: string;
  team_size_range?: string;
  annual_budget_range?: string;
  credit_denomination?: number;
  wallet_balance?: number;
  role?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

export interface DashboardMetrics {
  total_balance?: number;
  budget_allocated?: number;
  budget_used_percentage?: number;
  active_members_count?: number;
  books_completed_count?: number;
  company_rank?: number | string;
  spending_trend?: Array<{ month: string; amount: number }>;
  top_spenders?: Array<{ id: string | number; name: string; amount: number; books_count?: number }>;
  [key: string]: any;
}

export interface SpendingMetrics {
  total_spent?: number;
  by_department?: Record<string, number>;
  monthly_breakdown?: Array<{ month: string; amount: number }>;
  transactions_count?: number;
  books_count?: number;
  [key: string]: any;
}

export interface ActivityItem {
  id: string | number;
  type?: 'credit' | 'debit' | string;
  user_name?: string;
  title?: string;
  description?: string;
  meta?: string;
  amount?: number;
  books_count?: number;
  created_at?: string;
  time_ago?: string;
  [key: string]: any;
}

export interface LeaderboardUser {
  rank: number;
  id?: string | number;
  user_id?: string | number;
  name: string;
  email?: string;
  avatar?: string;
  avatar_bg?: string;
  department?: string;
  books_purchased?: number;
  books_completed?: number;
  streak_days?: number;
  time_spent?: string;
  is_current_user?: boolean;
}

export interface OrganizationMember {
  id: string | number;
  user_id?: string | number;
  name?: string;
  email?: string;
  invited_name?: string;
  invited_email?: string;
  role: 'employee' | 'member' | 'manager' | 'admin' | string;
  department?: string;
  spending_limit?: number;
  spent_amount?: number;
  spending_used?: number;
  books_count?: number;
  books_purchased_count?: number;
  status: 'active' | 'pending' | 'revoked' | string;
  invited_at?: string;
  joined_at?: string;
  avatar?: string;
  user?: {
    id: number | string;
    name?: string;
    email?: string;
    avatar?: string | null;
  };
  [key: string]: any;
}

export interface WalletSummary {
  balance: number;
  discount_percentage: number;
  lifetime_recharge_total: number;
  currency?: string;
  next_refill_date?: string;
}

export interface LedgerTransaction {
  id: string | number;
  type: 'credit' | 'debit';
  title: string;
  description?: string;
  reference_id?: string;
  amount: number;
  created_at: string;
  performed_by?: string;
}

export interface InvitationMetadata {
  token?: string;
  organization_name?: string;
  invited_name?: string;
  invited_email?: string;
  organization?: {
    id: string | number;
    name: string;
    code?: string;
    brand_name?: string;
  };
  name?: string;
  email?: string;
  role?: string;
  invited_by?: {
    name?: string;
    email?: string;
  };
  expires_at?: string;
  is_valid?: boolean;
  [key: string]: any;
}

// -------------------------------------------------------------
// 1. Profile & Dashboard Services
// -------------------------------------------------------------

export const getOrganizationMe = async (): Promise<OrganizationProfile> => {
  const response = await apiClient.get(Endpoints.organization.me);
  return response.data?.data || response.data;
};

export const getOrganizationDetails = async (): Promise<OrganizationProfile> => {
  const response = await apiClient.get(Endpoints.organization.details);
  return response.data?.data || response.data;
};

export const getOrganizationDashboard = async (): Promise<DashboardMetrics> => {
  const response = await apiClient.get(Endpoints.organization.dashboard);
  return response.data?.data || response.data;
};

export const getOrganizationSpending = async (): Promise<SpendingMetrics> => {
  const response = await apiClient.get(Endpoints.organization.spending);
  return response.data?.data || response.data;
};

export const getOrganizationActivity = async (page = 1): Promise<any[]> => {
  const response = await apiClient.get(Endpoints.organization.activity, {
    params: { page },
  });
  const data = response.data?.data || response.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
};

export const getOrganizationActivityPaginated = async (
  page = 1,
): Promise<{
  activities: any[];
  total?: number;
  current_page?: number;
  per_page?: number;
  last_page?: number;
}> => {
  const response = await apiClient.get(Endpoints.organization.activity, {
    params: { page },
  });
  const rawData = response.data;
  const data = rawData?.data || rawData;
  const meta = rawData?.meta || data?.meta;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.activities)
    ? data.activities
    : [];

  return {
    activities: list,
    total: meta?.total ?? data?.total ?? list.length,
    current_page: meta?.current_page ?? data?.current_page ?? page,
    per_page: meta?.per_page ?? data?.per_page ?? 10,
    last_page: meta?.last_page ?? data?.last_page,
  };
};

export const getOrganizationLeaderboard = async (): Promise<any[]> => {
  const response = await apiClient.get(Endpoints.organization.leaderboard);
  const data = response.data?.data || response.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
};

// -------------------------------------------------------------
// 2. Onboarding Services
// -------------------------------------------------------------

export const onboardingCheckName = async (name: string) => {
  const response = await apiClient.post(Endpoints.organization.checkName, { name });
  return response.data;
};

export const onboardingSetupProfile = async (data: {
  name: string;
  org_type?: string;
  gst_number?: string;
}) => {
  const response = await apiClient.post(Endpoints.organization.setupProfile, data);
  return response.data;
};

export const onboardingStep1 = async (data: {
  department?: string;
  name?: string;
  code?: string;
  brand_name?: string;
  gst_number?: string;
}) => {
  const response = await apiClient.post(Endpoints.organization.step1, data);
  return response.data;
};

export const onboardingStep2 = async (data: {
  team_size_range: string;
  org_type?: string;
}) => {
  const response = await apiClient.post(Endpoints.organization.step2, data);
  return response.data;
};

export const onboardingStep3 = async (data: {
  annual_budget_range: string;
  credit_denomination?: number;
}) => {
  const response = await apiClient.post(Endpoints.organization.step3, data);
  return response.data;
};

export const onboardingComplete = async (payload: {
  name?: string;
  code?: string;
  brand_name?: string;
  gst_number?: string;
  org_type?: string;
  department?: string;
  team_size_range?: string;
  annual_budget_range?: string;
  credit_denomination?: number;
}) => {
  const response = await apiClient.post(Endpoints.organization.onboarding, payload);
  return response.data;
};

// -------------------------------------------------------------
// 3. Invitations & Joining Services
// -------------------------------------------------------------

export const validateInvitationToken = async (
  token: string,
): Promise<InvitationMetadata> => {
  const response = await apiClient.post(
    Endpoints.organization.validateInvitation,
    { token },
  );
  return response.data?.data || response.data;
};

export const validateInvitationPayload = async (
  token: string,
): Promise<InvitationMetadata> => {
  const response = await apiClient.post(Endpoints.organization.validateInvitation, {
    token,
  });
  return response.data?.data || response.data;
};

export const acceptInvitation = async (
  token: string,
): Promise<{ success: boolean; message?: string; [key: string]: any }> => {
  const response = await apiClient.post(
    Endpoints.organization.acceptInvitation,
    { token },
  );
  return response.data;
};

export const joinOrganizationByCode = async (
  code: string,
): Promise<{ success: boolean; message?: string; [key: string]: any }> => {
  const response = await apiClient.post(Endpoints.organization.joinByCode(code));
  return response.data;
};

// -------------------------------------------------------------
// 4. Members Management Services
// -------------------------------------------------------------

export const getOrganizationMembers = async (params?: {
  page?: number;
  search?: string;
  role?: string;
}): Promise<{ members: OrganizationMember[]; total?: number; current_page?: number }> => {
  const response = await apiClient.get(Endpoints.organization.members, { params });
  const data = response.data?.data || response.data;
  if (Array.isArray(data)) {
    return { members: data, total: data.length };
  }
  return {
    members: data?.data || data?.members || [],
    total: data?.total || data?.count,
    current_page: data?.current_page || params?.page || 1,
  };
};

export const inviteOrganizationMember = async (payload: {
  email: string;
  name: string;
  role?: string;
  department?: string;
  spending_limit?: number;
}) => {
  const response = await apiClient.post(
    Endpoints.organization.inviteMember,
    payload,
  );
  return response.data;
};

export const bulkInviteMembers = async (members: Array<{
  email: string;
  name: string;
  role?: string;
  department?: string;
  spending_limit?: number;
}>) => {
  const response = await apiClient.post(Endpoints.organization.bulkInvite, {
    members,
  });
  return response.data;
};

export const bulkInviteCsv = async (file: File) => {
  const formData = new FormData();
  formData.append('csv_file', file);
  const response = await apiClient.post(
    Endpoints.organization.bulkInviteCsv,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const downloadSampleCsv = async (): Promise<Blob> => {
  const response = await apiClient.get(Endpoints.organization.sampleCsv, {
    responseType: 'blob',
  });
  return response.data;
};

export const reinviteMember = async (memberId: string | number) => {
  const response = await apiClient.post(
    Endpoints.organization.reinviteMember(memberId),
  );
  return response.data;
};

export const updateMemberSpendingLimit = async (
  memberId: string | number,
  spendingLimit: number,
) => {
  const response = await apiClient.post(
    Endpoints.organization.memberLimit(memberId),
    { spending_limit: spendingLimit },
  );
  return response.data;
};

export const updateMember = async (
  memberId: string | number,
  data: {
    role?: string;
    spending_limit?: number;
    status?: 'active' | 'pending' | 'revoked' | string;
    department?: string;
  },
) => {
  const response = await apiClient.patch(
    Endpoints.organization.memberById(memberId),
    data,
  );
  return response.data;
};

export const deleteMember = async (memberId: string | number) => {
  const response = await apiClient.delete(
    Endpoints.organization.memberById(memberId),
  );
  return response.data;
};

// -------------------------------------------------------------
// 5. Wallet & Recharges Services
// -------------------------------------------------------------

export const getWalletSummary = async (): Promise<WalletSummary> => {
  const response = await apiClient.get(Endpoints.organization.wallet);
  return response.data?.data || response.data;
};

export const getWalletLedger = async (
  page = 1,
): Promise<{
  transactions: LedgerTransaction[];
  total?: number;
  current_page?: number;
  per_page?: number;
  last_page?: number;
}> => {
  const response = await apiClient.get(Endpoints.organization.walletLedger, {
    params: { page },
  });
  const data = response.data?.data || response.data;
  if (Array.isArray(data)) {
    return { transactions: data, total: data.length, current_page: page, per_page: 10 };
  }
  const meta = response.data?.meta || data?.meta;
  const list =
    data?.data || data?.transactions || data?.ledger || (Array.isArray(data) ? data : []);
  return {
    transactions: Array.isArray(list) ? list : [],
    total: meta?.total ?? data?.total ?? (Array.isArray(list) ? list.length : 0),
    current_page: meta?.current_page ?? data?.current_page ?? page,
    per_page: meta?.per_page ?? data?.per_page ?? 10,
    last_page: meta?.last_page ?? data?.last_page,
  };
};

export const getRechargeHistory = async () => {
  const response = await apiClient.get(Endpoints.organization.walletRecharges);
  return response.data?.data || response.data || [];
};

export const initiateRecharge = async (amount: number, currency = 'INR') => {
  const response = await apiClient.post(Endpoints.organization.rechargeInitiate, {
    amount,
    currency,
  });
  return response.data?.data || response.data;
};

export const verifyRecharge = async (payload: {
  recharge_id: string | number;
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}) => {
  const response = await apiClient.post(
    Endpoints.organization.rechargeVerify,
    payload,
  );
  return response.data;
};

export const requestOrganizationTopUp = async (
  productId: string | number,
): Promise<{ success: boolean; message?: string; [key: string]: any }> => {
  const response = await apiClient.post(Endpoints.organization.requestTopUp, {
    product_id: productId,
  });
  return response.data;
};

// -------------------------------------------------------------
// 6. Purchasing & Checkout Services
// -------------------------------------------------------------

export const getPurchaseQuote = async (productId: number | string, quantity = 1) => {
  const response = await apiClient.post(Endpoints.organization.purchaseQuote, {
    product_id: productId,
    quantity,
  });
  return response.data?.data || response.data;
};

export const executeOrganizationPurchase = async (payload: {
  product_id?: number | string;
  quantity?: number;
  address_id?: number | string;
  cart_ids?: Array<number | string>;
  shipping_address?: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  };
}) => {
  const response = await apiClient.post(Endpoints.organization.purchase, payload);
  return response.data;
};

// -------------------------------------------------------------
// 7. Documents Management Services
// -------------------------------------------------------------

export const getOrganizationDocuments = async () => {
  const response = await apiClient.get(Endpoints.organization.documents);
  return response.data?.data || response.data || [];
};

export const uploadOrganizationDocument = async (file: File, type?: string) => {
  const formData = new FormData();
  formData.append('document', file);
  if (type) formData.append('type', type);
  const response = await apiClient.post(
    Endpoints.organization.documents,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const deleteOrganizationDocument = async (id: string | number) => {
  const response = await apiClient.delete(
    Endpoints.organization.documentById(id),
  );
  return response.data;
};

// -------------------------------------------------------------
// 8. Organization Leads
// -------------------------------------------------------------

export interface CreateOrganizationLeadPayload {
  country_code: string;
  phone: string;
  invited_by?: string;
}

export const createOrganizationLead = async (
  payload: CreateOrganizationLeadPayload,
) => {
  const response = await apiClient.post(Endpoints.organization.leads, payload);
  return response.data;
};

